import { makeObservable, observable } from 'mobx'

import { getClient } from 'api'
import { retry } from 'api/utils/retry'
import ClientDetailsError from 'error/clientDetailsError'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { HttpStatusCode } from 'utils/url/status'

interface Logos {
  client: string
  tuev: string
  ekomi: string
  ssl: string
  schufaNeutral: string
  guenstigerGehtNicht: string
}

interface Urls {
  ekomi: string
  imprint: string
  dataProtection: string
  termsAndConditions: string
  faq: string
  tuev: string
  precontractual: string
  declarationOfConsentAdvertising: string
}

interface ClientDetails {
  uuid: string
  email: string
  company: string
  street: string
  city: string
  country: string
  displayName: string
  zipCode: string
  phone: string
  workingTimes: string
  logos: Logos
  urls: Urls
}

interface ClientDetailsResponse {
  result: { client: ClientDetails }
}

const getClientDetails = async (tenantUuid: string) =>
  retry<ClientDetailsResponse>(
    getClient('apollo'),
    {
      method: 'get',
      url: `client/${tenantUuid}`
    },
    {
      maxRetryCount: 10,
      retryInterval: 300,
      condition: {
        error: (error) => error?.response?.status === HttpStatusCode.NotFound
      }
    }
  )

class ClientDetailsStore {
  public clientDetails: ClientDetails | null = null
  public clientDetailsFetched = false

  constructor() {
    makeObservable(this, {
      clientDetails: observable,
      clientDetailsFetched: observable
    })
  }

  fetchClientDetails = async (tenantUuid: string) => {
    try {
      const response = await getClientDetails(tenantUuid)

      const {
        data: {
          result: { client: clientDetailsResponse }
        }
      } = response

      if (!clientDetailsResponse) {
        logAndTrackError(
          new ClientDetailsError(
            tenantUuid,
            'Could not get client details',
            response.config,
            response
          )
        )
      }

      this.clientDetails = clientDetailsResponse
    } catch (err) {
      logAndTrackError(
        new ClientDetailsError(tenantUuid, 'Could not get client details', {})
      )
    } finally {
      this.clientDetailsFetched = true
    }
  }
}

export default ClientDetailsStore
